import React, { useState, useRef, useCallback, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Upload, Image as ImageIcon, AlertCircle, Check } from 'lucide-react'
import { useProjectStore } from '../../stores/projectStore'

interface ImageUploadDropzoneProps {
  open: boolean
  onClose: () => void
  sceneId: string | null
  projectId: string
}

interface UploadFile {
  file: File
  preview: string
  id: string
  status: 'pending' | 'uploading' | 'success' | 'error'
  progress: number
  error?: string
}

const ImageUploadDropzone: React.FC<ImageUploadDropzoneProps> = ({
  open,
  onClose,
  sceneId,
  projectId
}) => {
  const [files, setFiles] = useState<UploadFile[]>([])
  const [isDragOver, setIsDragOver] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { uploadImage } = useProjectStore()

  // Esc key handler
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open && !isUploading) {
        handleClose()
      }
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [open, isUploading])

  const generateId = () => Math.random().toString(36).substring(2, 15)

  const createUploadFile = (file: File): UploadFile => ({
    file,
    preview: URL.createObjectURL(file),
    id: generateId(),
    status: 'pending',
    progress: 0
  })

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles) return

    const validFiles = Array.from(selectedFiles).filter(file => {
      if (!file.type.startsWith('image/')) return false
      if (file.size > 10 * 1024 * 1024) return false // 10MB limit
      return true
    })

    const newFiles = validFiles.map(createUploadFile)
    setFiles(prev => [...prev, ...newFiles])
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    handleFileSelect(e.dataTransfer.files)
  }, [handleFileSelect])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const removeFile = (fileId: string) => {
    setFiles(prev => {
      const file = prev.find(f => f.id === fileId)
      if (file?.preview) {
        URL.revokeObjectURL(file.preview)
      }
      return prev.filter(f => f.id !== fileId)
    })
  }

  const uploadFiles = async () => {
    if (!sceneId || files.length === 0) return

    setIsUploading(true)

    const uploadPromises = files
      .filter(f => f.status === 'pending')
      .map(async (fileItem) => {
        try {
          // Update status to uploading
          setFiles(prev => prev.map(f => 
            f.id === fileItem.id 
              ? { ...f, status: 'uploading' as const, progress: 0 }
              : f
          ))

                                // Upload the image
           await uploadImage(fileItem.file, sceneId, projectId)

           // Mark as success
           setFiles(prev => prev.map(f => 
             f.id === fileItem.id 
               ? { ...f, status: 'success' as const, progress: 100 }
               : f
           ))

        } catch (error) {
          console.error('Upload failed:', error)
          setFiles(prev => prev.map(f => 
            f.id === fileItem.id 
              ? { 
                  ...f, 
                  status: 'error' as const, 
                  error: error instanceof Error ? error.message : 'Upload failed'
                }
              : f
          ))
        }
      })

    await Promise.all(uploadPromises)
    setIsUploading(false)

    // Auto-close if all uploads succeeded
    const allSucceeded = files.every(f => f.status === 'success')
    if (allSucceeded) {
      setTimeout(() => {
        handleClose()
      }, 1000)
    }
  }

  const handleClose = () => {
    // Cleanup object URLs
    files.forEach(file => {
      if (file.preview) {
        URL.revokeObjectURL(file.preview)
      }
    })
    setFiles([])
    setIsUploading(false)
    onClose()
  }

  if (!open) return null

  const allUploaded = files.length > 0 && files.every(f => f.status === 'success')
  const hasErrors = files.some(f => f.status === 'error')
  const pendingFiles = files.filter(f => f.status === 'pending').length

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleClose}
      >
        <motion.div
          className="glass-card w-full max-w-2xl max-h-[90vh] overflow-hidden border border-white/20"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                <Upload className="w-5 h-5 text-green-300" />
              </div>
              <h2 className="text-xl font-semibold text-white">Upload Images</h2>
            </div>
            <button
              onClick={handleClose}
              className="glass-button p-2 hover:bg-white/20"
              disabled={isUploading}
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {!sceneId ? (
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                <h3 className="text-white font-medium mb-2">No Scene Selected</h3>
                <p className="text-white/70">Please select a scene first to upload images.</p>
              </div>
            ) : (
              <>
                {/* Upload Area */}
                {files.length === 0 && (
                  <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                      isDragOver
                        ? 'border-primary-400 bg-primary-500/10'
                        : 'border-white/30 hover:border-white/50'
                    }`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                  >
                    <ImageIcon className="w-16 h-16 text-white/50 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">
                      Drop images here or click to browse
                    </h3>
                    <p className="text-white/70 mb-4">
                      Support JPEG, PNG, WebP up to 10MB each
                    </p>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="glass-button bg-primary-500/30 hover:bg-primary-500/40 text-white px-6 py-3"
                    >
                      Choose Files
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) => handleFileSelect(e.target.files)}
                      className="hidden"
                    />
                  </div>
                )}

                {/* File List */}
                {files.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-white font-medium">
                        Selected Images ({files.length})
                      </h3>
                      {!isUploading && pendingFiles > 0 && (
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="glass-button text-white/80 hover:bg-white/10 px-3 py-1 text-sm"
                        >
                          Add More
                        </button>
                      )}
                    </div>

                    <div className="max-h-60 overflow-y-auto space-y-2">
                      {files.map((file) => (
                        <div key={file.id} className="glass-card p-3 flex items-center space-x-3">
                          <img
                            src={file.preview}
                            alt={file.file.name}
                            className="w-12 h-12 object-cover rounded"
                          />
                          
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm truncate">{file.file.name}</p>
                            <p className="text-white/60 text-xs">
                              {(file.file.size / 1024 / 1024).toFixed(1)} MB
                            </p>
                            
                            {file.status === 'uploading' && (
                              <div className="w-full bg-white/20 rounded-full h-1 mt-1">
                                <div 
                                  className="bg-primary-400 h-1 rounded-full transition-all duration-300"
                                  style={{ width: `${file.progress}%` }}
                                />
                              </div>
                            )}
                          </div>

                          <div className="flex items-center space-x-2">
                            {file.status === 'pending' && (
                              <span className="text-white/60 text-xs">Pending</span>
                            )}
                            {file.status === 'uploading' && (
                              <span className="text-primary-300 text-xs">{file.progress}%</span>
                            )}
                            {file.status === 'success' && (
                              <Check className="w-4 h-4 text-green-400" />
                            )}
                            {file.status === 'error' && (
                              <AlertCircle className="w-4 h-4 text-red-400" />
                            )}
                            
                            {!isUploading && file.status !== 'success' && (
                              <button
                                onClick={() => removeFile(file.id)}
                                className="glass-button p-1 hover:bg-red-500/20 text-red-300"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) => handleFileSelect(e.target.files)}
                      className="hidden"
                    />
                  </div>
                )}

                {/* Status Messages */}
                {hasErrors && (
                  <div className="glass-card bg-red-500/20 border border-red-500/30 p-4">
                    <div className="flex items-center space-x-2 text-red-200">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-sm">Some uploads failed. Please try again.</span>
                    </div>
                  </div>
                )}

                {allUploaded && (
                  <div className="glass-card bg-green-500/20 border border-green-500/30 p-4">
                    <div className="flex items-center space-x-2 text-green-200">
                      <Check className="w-4 h-4" />
                      <span className="text-sm">All images uploaded successfully!</span>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-between pt-4">
                  <button
                    onClick={handleClose}
                    className="glass-button text-white/90 hover:bg-white/10 px-6 py-2"
                    disabled={isUploading}
                  >
                    {allUploaded ? 'Done' : 'Cancel'}
                  </button>

                  {pendingFiles > 0 && (
                    <button
                      onClick={uploadFiles}
                      className="glass-button bg-green-500/30 hover:bg-green-500/40 text-white font-semibold px-6 py-2 disabled:opacity-50"
                      disabled={isUploading || !sceneId}
                    >
                      {isUploading ? 'Uploading...' : `Upload ${pendingFiles} Image${pendingFiles !== 1 ? 's' : ''}`}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default ImageUploadDropzone 